import type { Locale } from '@/i18n'

/**
 * Числа, деньги и даты в формате языка интерфейса.
 *
 * `1410000` на доске читается с запинкой — сколько там нулей? — а `2026-10-01` выглядит
 * машинной меткой, не сроком. Формат берётся из `Intl` по языку пользователя, а не
 * собирается руками: разделитель разрядов, место знака валюты и порядок дня и месяца у
 * языков разные, и своя реализация верна ровно для одного.
 */

/** Целое или дробное число с разделителями разрядов. */
export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
}

/**
 * A ratio or coefficient with a fixed number of digits (`12,5`). Not for money (that is
 * `formatMoney`) and not for quantities (`formatQuantity`): kept here so that screens
 * never reach for `Intl.NumberFormat` themselves (#91).
 */
/** Share of a whole (0.25 → «25 %»), up to {@code maxDigits} fraction digits. */
export function formatPercent(ratio: number, maxDigits: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: maxDigits }).format(ratio)
}

export function formatDecimal(value: number, digits: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}

// Money lives in one module for the whole UI (#91); these names are kept so that screens
// importing them from here format exactly like the list, the card and the board.
export { formatMoney, formatMoneyTotals as formatTotals, formatQuantity } from '@/format/money'

/**
 * Календарная дата (`YYYY-MM-DD`) в формате языка: `01.10.2026`.
 *
 * Разбирается в местную полночь, а не через `new Date(iso)`: строка без времени
 * читается как полночь UTC, и западнее Гринвича срок «1 октября» показывался бы
 * 30 сентября.
 */
export function formatDate(value: string, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}
